import { FC, useState, useEffect } from 'react';

import classNames from 'classnames';
import { usePopper } from 'react-popper';
import { XIcon } from '@heroicons/react/outline';
import { Listbox, Portal } from '@headlessui/react';
import { Controller, UseFormWatch, useFormContext } from 'react-hook-form';

import {
  PatientDataPermission,
  ProviderUserMemberDataFragment,
  ProviderUserRole,
  SeatType,
} from '../../../../../../generated/graphql';

import {
  AddTeamMembersFormData,
  MemberField,
} from '../../../../../types/teamMembers';

import { emailRegisterOptions } from '../../../../../lib/register-options';

import ChevronRight from '../../../../../svgs/ChevronRight';

import InputGroup from '../../../../../components/InputGroup';
import IconButton from '../../../../../components/IconButton';

import {
  PatientDataPermissionOptions,
  ProviderUserRoleOptions,
  getLabelFromPatientDataPermission,
  SeatTypeOptions,
} from '../../TeamMembersTable/helpers';

interface TeamMemberInputRowProps {
  fieldIndex: number;
  formFields: MemberField[];
  handleRemoveRow: (index: number) => void;
  existingTeamMembers: ProviderUserMemberDataFragment[];
  watch: UseFormWatch<AddTeamMembersFormData>;
  isSeatTypeEnabled?: boolean;
  seatUsage?: {
    remainingStaffSeats?: number | null;
    remainingCollaboratorSeats?: number | null;
  } | null;
}

const TeamMemberInputRow: FC<TeamMemberInputRowProps> = ({
  fieldIndex,
  formFields,
  handleRemoveRow,
  existingTeamMembers,
  watch,
  isSeatTypeEnabled = false,
  seatUsage,
}) => {
  const {
    control,
    register,
    setValue,
    formState: { errors: formErrors },
  } = useFormContext();

  const emailKey: `members.${number}.email` = `members.${fieldIndex}.email`;
  const roleKey: `members.${number}.role` = `members.${fieldIndex}.role`;
  const patientDataPermissionKey: `members.${number}.patientDataPermission` = `members.${fieldIndex}.patientDataPermission`;
  const seatTypeKey: `members.${number}.seatType` = `members.${fieldIndex}.seatType`;

  const watchRole = watch(roleKey);

  const [roleElement, setRoleElement] = useState(null);
  const [rolePopperElement, setRolePopperElement] = useState(null);

  const { styles: roleStyles, attributes: roleAttributes } = usePopper(
    roleElement,
    rolePopperElement,
    {
      placement: 'bottom-end',
      strategy: 'fixed',
      modifiers: [{ name: 'offset', options: { offset: [0, 5] } }],
    },
  );

  const [patientDataPermissionElement, setPatientDataPermissionElement] =
    useState(null);

  const [
    patientDataPermissionElementPopperElement,
    setPatientDataPermissionElementPopperElement,
  ] = useState(null);

  const {
    styles: patientDataPermissionStyles,
    attributes: patientDataPermissionAttributes,
  } = usePopper(
    patientDataPermissionElement,
    patientDataPermissionElementPopperElement,
    {
      placement: 'bottom-end',
      strategy: 'fixed',
      modifiers: [{ name: 'offset', options: { offset: [0, 5] } }],
    },
  );

  const [seatTypeElement, setSeatTypeElement] = useState(null);
  const [seatTypePopperElement, setSeatTypePopperElement] = useState(null);

  const { styles: seatTypeStyles, attributes: seatTypeAttributes } = usePopper(
    seatTypeElement,
    seatTypePopperElement,
    {
      placement: 'bottom-end',
      strategy: 'fixed',
      modifiers: [{ name: 'offset', options: { offset: [0, 5] } }],
    },
  );

  const validateEmail = (email: string) => {
    if (
      formFields.some(
        (field, index) => index < fieldIndex && field.email === email,
      )
    ) {
      return 'Email has already been entered';
    }

    if (existingTeamMembers.some((member) => member.email === email)) {
      return 'Already on your team';
    }

    return true;
  };

  // Check which seat types are available (but don't filter them out)
  const isStaffAvailable = (seatUsage?.remainingStaffSeats ?? 0) > 0;
  const isCollaboratorAvailable =
    (seatUsage?.remainingCollaboratorSeats ?? 0) > 0;

  // Auto-update seat type if current selection becomes unavailable
  useEffect(() => {
    if (isSeatTypeEnabled) {
      const currentSeatType = watch(seatTypeKey);
      const isCurrentSeatTypeAvailable =
        (currentSeatType === SeatType.Staff && isStaffAvailable) ||
        (currentSeatType === SeatType.Collaborator && isCollaboratorAvailable);

      if (!isCurrentSeatTypeAvailable) {
        // Set to first available option
        if (isStaffAvailable) {
          setValue(seatTypeKey, SeatType.Staff);
        } else if (isCollaboratorAvailable) {
          setValue(seatTypeKey, SeatType.Collaborator);
        }
      }
    }
  }, [
    isStaffAvailable,
    isCollaboratorAvailable,
    isSeatTypeEnabled,
    seatTypeKey,
    watch,
    setValue,
  ]);

  return (
    <div
      key={`team-member-input-row-${fieldIndex}`}
      className="flex w-full items-center gap-x-3"
    >
      <InputGroup
        label="Email"
        placeholder=""
        containerClassName="w-7/12"
        inputSize="small"
        labelHidden
        required
        errorMessage={formErrors.members?.[fieldIndex]?.email?.message}
        errorLocation="right"
        {...register(emailKey, {
          ...emailRegisterOptions,
          validate: validateEmail,
        })}
      />
      <Controller
        name={roleKey}
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <Listbox
            as="div"
            value={field.value}
            onChange={field.onChange}
            className="relative w-3/12"
          >
            {({ open }) => (
              <>
                <Listbox.Button
                  className={classNames(
                    'relative flex w-full flex-row items-center justify-between focus:ring-secondary-50',
                    'max-w-[24rem] rounded-md bg-neutral-50 p-3 text-left shadow-sm sm:text-caption',
                    'focus:outline-green-50 focus:ring-0 ',
                  )}
                  ref={setRoleElement}
                >
                  <div>{field.value}</div>
                  {field.value !== ProviderUserRole.Owner && (
                    <div className="flex items-center pl-2">
                      <ChevronRight
                        className={classNames(
                          open ? '-rotate-90' : 'rotate-90',
                        )}
                      />
                    </div>
                  )}
                </Listbox.Button>
                <Portal>
                  <Listbox.Options
                    className="absolute z-50 w-[24rem] divide-y divide-neutral-75 rounded-md border border-neutral-75 bg-white shadow-lg focus:outline-none"
                    ref={setRolePopperElement}
                    style={roleStyles.popper}
                    {...roleAttributes.popper}
                  >
                    {ProviderUserRoleOptions.map((roleOption, index) => (
                      <Listbox.Option
                        key={`roleOption_${roleOption.role}`}
                        value={roleOption.role}
                        className={({ active }) =>
                          classNames(
                            'cursor-pointer px-4 py-5',
                            active && 'bg-neutral-25',
                            {
                              'rounded-t-md': active && index === 0, // round top if it's the first item
                              'rounded-b-md':
                                active &&
                                index === ProviderUserRoleOptions.length - 1, // round bottom if it's the last item
                            },
                          )
                        }
                      >
                        <div className="text-body font-medium text-neutral-125">
                          {roleOption.role}
                        </div>
                        <div className="text-small-caption text-neutral-125">
                          {roleOption.description}
                        </div>
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </Portal>
              </>
            )}
          </Listbox>
        )}
      />
      <Controller
        name={patientDataPermissionKey}
        control={control}
        rules={{ required: true }}
        render={({ field: patientDataPermissionField }) => (
          <Listbox
            as="div"
            value={patientDataPermissionField.value}
            onChange={patientDataPermissionField.onChange}
            className={`relative ${isSeatTypeEnabled ? 'w-4/12' : 'w-7/12'}`}
          >
            {({ open }) => (
              <>
                <Listbox.Button
                  className={classNames(
                    'relative flex w-full flex-row items-center justify-between',
                    'max-w-[24rem] rounded-md bg-neutral-50 p-3 text-left shadow-sm sm:text-caption',
                    'focus:outline-green-50 focus:ring-0',
                  )}
                  ref={setPatientDataPermissionElement}
                >
                  <div>
                    {
                      getLabelFromPatientDataPermission(
                        patientDataPermissionField.value,
                      ).label
                    }
                  </div>
                  <div className="flex items-center pl-2">
                    <ChevronRight
                      className={classNames(open ? '-rotate-90' : 'rotate-90')}
                    />
                  </div>
                </Listbox.Button>
                <Portal>
                  <Listbox.Options
                    className="absolute z-50 w-[24rem] divide-y divide-neutral-75 rounded-md border border-neutral-75 bg-white shadow-lg focus:outline-none"
                    ref={setPatientDataPermissionElementPopperElement}
                    style={patientDataPermissionStyles.popper}
                    {...patientDataPermissionAttributes.popper}
                  >
                    {PatientDataPermissionOptions.filter(
                      ({ patientDataPermission }) =>
                        watchRole !== ProviderUserRole.Practitioner ||
                        patientDataPermission !== PatientDataPermission.None,
                    ).map((patientDataPermission, index) => (
                      <Listbox.Option
                        key={`patientDataPermission_${patientDataPermission.patientDataPermission}`}
                        value={patientDataPermission.patientDataPermission}
                        className={({ active }) =>
                          classNames(
                            'cursor-pointer px-4 py-5',
                            active && 'bg-neutral-25',
                            {
                              'rounded-t-md': active && index === 0, // round top if it's the first item
                              'rounded-b-md':
                                active &&
                                index ===
                                  PatientDataPermissionOptions.length - 1, // round bottom if it's the last item
                            },
                          )
                        }
                      >
                        <div className="text-body font-medium text-neutral-125">
                          {patientDataPermission.label}
                        </div>
                        <div className="text-small-caption text-neutral-125">
                          {patientDataPermission.description}
                        </div>
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </Portal>
              </>
            )}
          </Listbox>
        )}
      />

      {isSeatTypeEnabled && (
        <Controller
          name={seatTypeKey}
          control={control}
          rules={{ required: true }}
          render={({ field: seatTypeField }) => (
            <Listbox
              as="div"
              value={seatTypeField.value}
              onChange={seatTypeField.onChange}
              className="relative w-4/12"
            >
              {({ open }) => (
                <>
                  <Listbox.Button
                    className={classNames(
                      'relative flex w-full flex-row items-center justify-between',
                      'max-w-[24rem] rounded-md bg-neutral-50 p-3 text-left shadow-sm sm:text-caption',
                      'focus:outline-green-50 focus:ring-0',
                    )}
                    ref={setSeatTypeElement}
                  >
                    <div>
                      {
                        SeatTypeOptions.find(
                          (option) => option.seatType === seatTypeField.value,
                        )?.label
                      }
                    </div>
                    <div className="flex items-center pl-2">
                      <ChevronRight
                        className={classNames(
                          open ? '-rotate-90' : 'rotate-90',
                        )}
                      />
                    </div>
                  </Listbox.Button>
                  <Portal>
                    <Listbox.Options
                      className="absolute z-50 w-[24rem] divide-y divide-neutral-75 rounded-md border border-neutral-75 bg-white shadow-lg focus:outline-none"
                      ref={setSeatTypePopperElement}
                      style={seatTypeStyles.popper}
                      {...seatTypeAttributes.popper}
                    >
                      {SeatTypeOptions.map((seatTypeOption, index) => {
                        const isFirst = index === 0;
                        const isLast = index === SeatTypeOptions.length - 1;
                        const isDisabled =
                          (seatTypeOption.seatType === SeatType.Staff &&
                            !isStaffAvailable) ||
                          (seatTypeOption.seatType === SeatType.Collaborator &&
                            !isCollaboratorAvailable);

                        return (
                          <Listbox.Option
                            key={`seatType_${seatTypeOption.seatType}`}
                            value={seatTypeOption.seatType}
                            disabled={isDisabled}
                            className={({ active, disabled }) =>
                              classNames('px-4 py-5', {
                                'cursor-pointer': !disabled,
                                'cursor-not-allowed opacity-50': disabled,
                                'bg-neutral-25': active && !disabled,
                                'rounded-t-md': active && isFirst && !disabled,
                                'rounded-b-md': active && isLast && !disabled,
                              })
                            }
                          >
                            <div className="text-body font-medium text-neutral-125">
                              {seatTypeOption.label}
                            </div>
                            <div className="text-small-caption text-neutral-125">
                              {seatTypeOption.description}
                            </div>
                          </Listbox.Option>
                        );
                      })}
                    </Listbox.Options>
                  </Portal>
                </>
              )}
            </Listbox>
          )}
        />
      )}

      <IconButton
        IconComponent={XIcon}
        aria-label="Remove row"
        iconClassName="h-4 w-4"
        className="rounded-lg p-2"
        disabled={formFields.length <= 1}
        onClick={() => handleRemoveRow(fieldIndex)}
      />
    </div>
  );
};

export default TeamMemberInputRow;
